import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { VisibleArea, fitVertically, visibleArea } from './visible-area';

/**
 * Keeps a dialog's overlay pane inside the part of the screen above a soft keyboard, so its composer can be seen
 * while typing. Mobile Safari covers the foot of the page with the keyboard rather than resizing it, so a dialog
 * sized to the screen (80vh, centred) keeps its message box under the keyboard. While the keyboard is up the pane is
 * shortened and moved up to fit; when it closes the pane goes back to its own size and place.
 *
 * Put it on the dialog component's root element.
 */
@Directive({ selector: '[appFitAboveKeyboard]', standalone: true })
export class FitAboveKeyboardDirective implements AfterViewInit, OnDestroy {
  private pane?: HTMLElement;
  private fitted = false;
  private ownHeight = '';
  private readonly fit = (): void => this.fitToVisibleArea();

  constructor(private readonly el: ElementRef<HTMLElement>, private readonly zone: NgZone) {}

  ngAfterViewInit(): void {
    this.pane = this.el.nativeElement.closest<HTMLElement>('.cdk-overlay-pane') ?? undefined;
    if (!this.pane) return;
    this.zone.runOutsideAngular(() => {
      window.addEventListener('resize', this.fit);
      window.visualViewport?.addEventListener('resize', this.fit);
      window.visualViewport?.addEventListener('scroll', this.fit);
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.fit);
    window.visualViewport?.removeEventListener('resize', this.fit);
    window.visualViewport?.removeEventListener('scroll', this.fit);
  }

  private fitToVisibleArea(): void {
    const pane = this.pane;
    if (!pane) return;
    // The app's styles animate a pane's height and transform; measured mid-animation it would be in the wrong place.
    const transition = pane.style.transition;
    pane.style.transition = 'none';
    this.place(pane);
    pane.getBoundingClientRect();
    pane.style.transition = transition;
  }

  private place(pane: HTMLElement): void {
    // Measure the pane where it would be without the keyboard, then fit that.
    if (this.fitted) {
      pane.style.height = this.ownHeight;
      pane.style.transform = '';
      this.fitted = false;
    }
    const rect = pane.getBoundingClientRect();
    const box = fitVertically({ top: rect.top, height: rect.height }, this.visible(), 8);
    if (box.top === rect.top && box.height === rect.height) return;
    this.ownHeight = pane.style.height;
    this.fitted = true;
    pane.style.height = `${box.height}px`;
    // A centred pane re-centres when it gets shorter, so the shift is measured after the new height.
    pane.style.transform = `translateY(${box.top - pane.getBoundingClientRect().top}px)`;
  }

  private visible(): VisibleArea {
    return visibleArea();
  }
}
